/**
 * DxfPreview — Renders a lightweight SVG preview of a DXF file client-side.
 * Falls back to metadata-only card for DWG/PDF/ZIP formats.
 *
 * DXF format: text-based CAD interchange. Uses dxf-parser to extract entities
 * (LINE, LWPOLYLINE, POLYLINE, CIRCLE, ARC, ELLIPSE, SPLINE) and draws them into
 * a viewBox-normalized SVG. No 3D — 2D orthographic projection to XY plane.
 */
import { useEffect, useState } from "react";
import DxfParser from "dxf-parser";
import { Loader2, AlertTriangle, FileWarning, Ruler } from "lucide-react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/** Convert parsed DXF entities into SVG path/element strings + bounding box. */
function entitiesToSvg(entities) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const shapes = [];
  const push = (x, y) => {
    if (Number.isFinite(x) && Number.isFinite(y)) {
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    }
  };

  for (const e of entities || []) {
    switch (e.type) {
      case "LINE": {
        const s = e.vertices?.[0] || e.start;
        const t = e.vertices?.[1] || e.end;
        if (!s || !t) break;
        push(s.x, s.y); push(t.x, t.y);
        shapes.push(`<line x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}" />`);
        break;
      }
      case "LWPOLYLINE":
      case "POLYLINE": {
        const verts = e.vertices || [];
        if (!verts.length) break;
        verts.forEach(v => push(v.x, v.y));
        const pts = verts.map(v => `${v.x},${v.y}`).join(" ");
        const closeAttr = e.shape || e.closed ? "polygon" : "polyline";
        shapes.push(`<${closeAttr} points="${pts}" fill="none" />`);
        break;
      }
      case "CIRCLE": {
        const { center: c, radius: r } = e;
        if (!c || !Number.isFinite(r)) break;
        push(c.x - r, c.y - r); push(c.x + r, c.y + r);
        shapes.push(`<circle cx="${c.x}" cy="${c.y}" r="${r}" fill="none" />`);
        break;
      }
      case "ARC": {
        const { center: c, radius: r, startAngle: sa, endAngle: ea } = e;
        if (!c || !Number.isFinite(r)) break;
        push(c.x - r, c.y - r); push(c.x + r, c.y + r);
        const s = { x: c.x + r * Math.cos(sa), y: c.y + r * Math.sin(sa) };
        const t = { x: c.x + r * Math.cos(ea), y: c.y + r * Math.sin(ea) };
        let delta = ea - sa; if (delta < 0) delta += Math.PI * 2;
        const largeArc = delta > Math.PI ? 1 : 0;
        shapes.push(`<path d="M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${t.x} ${t.y}" fill="none" />`);
        break;
      }
      case "ELLIPSE": {
        const { center: c, majorAxisEndPoint: mae, axisRatio: ar } = e;
        if (!c || !mae) break;
        const rx = Math.hypot(mae.x, mae.y);
        const ry = rx * (ar || 1);
        const rot = (Math.atan2(mae.y, mae.x) * 180) / Math.PI;
        push(c.x - rx, c.y - ry); push(c.x + rx, c.y + ry);
        shapes.push(`<ellipse cx="${c.x}" cy="${c.y}" rx="${rx}" ry="${ry}" transform="rotate(${rot} ${c.x} ${c.y})" fill="none" />`);
        break;
      }
      case "SPLINE": {
        const cps = e.controlPoints || [];
        if (cps.length < 2) break;
        cps.forEach(p => push(p.x, p.y));
        const d = cps.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
        shapes.push(`<path d="${d}" fill="none" />`);
        break;
      }
      default:
        break;
    }
  }
  if (!Number.isFinite(minX)) return { shapes: [], bbox: null };
  return {
    shapes,
    bbox: { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY },
  };
}

export default function DxfPreview({ file }) {
  const [state, setState] = useState({ loading: false, error: null, svg: null, bbox: null, entityCount: 0, unsupported: false });

  useEffect(() => {
    if (!file) { setState({ loading: false, error: null, svg: null, bbox: null, entityCount: 0, unsupported: false }); return; }
    const name = (file.name || "").toLowerCase();
    // Unsupported formats: show placeholder card
    if (!name.endsWith(".dxf")) {
      setState({ loading: false, error: null, svg: null, bbox: null, entityCount: 0, unsupported: true });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parser = new DxfParser();
        const dxf = parser.parseSync(reader.result);
        const { shapes, bbox } = entitiesToSvg(dxf.entities || []);
        if (!shapes.length || !bbox) {
          setState({ loading: false, error: "Nessuna geometria supportata trovata nel file.", svg: null, bbox: null, entityCount: 0, unsupported: false });
          return;
        }
        const pad = clamp(Math.max(bbox.w, bbox.h) * 0.04, 0.5, 200);
        const viewBox = `${bbox.minX - pad} ${bbox.minY - pad} ${bbox.w + pad * 2} ${bbox.h + pad * 2}`;
        // Flip Y (DXF Y-up → SVG Y-down)
        const strokeWidth = Math.max(bbox.w, bbox.h) * 0.003 || 0.5;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet"><g transform="matrix(1 0 0 -1 0 ${2 * (bbox.minY) + bbox.h})" stroke="#ff4b00" stroke-width="${strokeWidth}" fill="none">${shapes.join("")}</g></svg>`;
        setState({ loading: false, error: null, svg, bbox, entityCount: shapes.length, unsupported: false });
      } catch (err) {
        console.warn("DXF parse error:", err);
        setState({ loading: false, error: "Impossibile leggere il DXF. Prova a esportare 'AutoCAD DXF (R2018)' dal tuo CAD.", svg: null, bbox: null, entityCount: 0, unsupported: false });
      }
    };
    reader.onerror = () => setState({ loading: false, error: "Errore lettura file.", svg: null, bbox: null, entityCount: 0, unsupported: false });
    reader.readAsText(file);
  }, [file]);

  if (!file) return null;

  return (
    <div className="mt-4 border border-[#1a1a1a] bg-[#0a0a0a]" data-testid="dxf-preview">
      <div className="px-4 py-2 border-b border-[#1a1a1a] flex items-center justify-between">
        <div className="font-mono-tech text-[10px] text-white/50 uppercase">Anteprima pezzo</div>
        {state.bbox && (
          <div className="font-mono-tech text-[10px] text-white/60 flex items-center gap-2" data-testid="dxf-bbox">
            <Ruler size={11} className="text-[#ff4b00]" />
            {state.bbox.w.toFixed(1)} × {state.bbox.h.toFixed(1)} unità · {state.entityCount} entità
          </div>
        )}
      </div>
      <div className="p-4 bg-black min-h-[260px] flex items-center justify-center relative">
        {state.loading && (
          <div className="text-white/50 flex flex-col items-center gap-2">
            <Loader2 size={20} className="animate-spin text-[#ff4b00]" />
            <span className="font-mono-tech text-[10px]">Analisi DXF…</span>
          </div>
        )}
        {state.unsupported && (
          <div className="text-center text-white/60 py-6" data-testid="dxf-preview-unsupported">
            <FileWarning size={28} className="mx-auto mb-3 text-white/40" />
            <div className="font-mono-tech text-[11px]">Anteprima disponibile solo per file <b className="text-[#ff4b00]">.DXF</b></div>
            <div className="font-mono-tech text-[10px] text-white/40 mt-2">DWG/PDF/ZIP saranno esaminati dal tecnico al ricevimento.</div>
          </div>
        )}
        {state.error && (
          <div className="text-center text-white/60 py-6">
            <AlertTriangle size={28} className="mx-auto mb-3 text-orange-400" />
            <div className="font-mono-tech text-[11px]">{state.error}</div>
          </div>
        )}
        {state.svg && (
          <div
            className="w-full h-[280px] flex items-center justify-center"
            data-testid="dxf-preview-svg"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: state.svg }}
          />
        )}
      </div>
    </div>
  );
}
